import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';

import { PricingTier, usePublicPricingTiers } from '../../hooks/usePublicPricingTiers';
import { IN_APP_FOOTER_LINKS } from './publicFooterLinks';

// Prices must match the Paddle catalog exactly. Amounts come from the same DB
// subscription_tiers that back the Paddle prices, served by the public
// /billing/subscription/tiers endpoint.
const TAX_NOTE = 'Prices in USD. Taxes may apply and will be calculated at checkout.';
// New accounts receive free signup credits enforced in-app (no card, no charge). Paddle is
// only used when a customer subscribes, at the monthly price shown below.
const TRIAL_NOTE =
  'New to Kolega Studio? Every new account starts with free signup credits (around $7.50 of ' +
  'usage, valid for 14 days) so you can try the platform before subscribing — no card required. ' +
  'When you subscribe, you are billed monthly at the plan price shown (charged at checkout).';

interface DisplayPlan {
  name: string;
  description: string;
  priceLabel: string;
  cadence: string;
  features: string[];
  cta: { label: string; href: string };
  featured?: boolean;
  comingSoon?: boolean;
}

function tierToPlan(tier: PricingTier): DisplayPlan {
  // Sales-led tiers (e.g. Custom) have no self-serve price; they route to the contact page.
  if (tier.metadata?.requires_sales_contact) {
    return {
      name: tier.display_name,
      description: tier.description || '',
      priceLabel: 'Custom',
      cadence: '',
      features: tier.features,
      cta: { label: 'Contact Sales', href: '/contact' },
    };
  }
  return {
    name: tier.display_name,
    description: tier.description || '',
    priceLabel: `$${tier.price}`,
    cadence: '/month',
    features: tier.features,
    // The app handles login/signup from the home page.
    cta: { label: 'Get started', href: '/' },
    featured: tier.name === 'pro' && tier.is_active,
    comingSoon: !tier.is_active,
  };
}

function PublicPageShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950 text-neutral-900 dark:text-white flex flex-col">
      <header className="border-b border-neutral-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="text-base sm:text-lg font-bold flex items-center">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-1.5 sm:mr-2">
              <path d="M7 8L3 12L7 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M17 8L21 12L17 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10 20L14 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Kolega</span>
            <span className="font-light">Studio</span>
          </Link>
          <Link
            to="/"
            className="bg-brand-primary text-black text-sm font-medium px-4 py-2 rounded-lg hover:bg-brand-primaryDark transition-colors"
          >
            Sign in
          </Link>
        </div>
      </header>
      <div className="flex-1">{children}</div>
      <footer className="border-t border-neutral-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-neutral-600 dark:text-neutral-400">
          {IN_APP_FOOTER_LINKS.map((link) => (
            <Link key={link.href} to={link.href} className="hover:text-neutral-900 dark:hover:text-white transition-colors">
              {link.label}
            </Link>
          ))}
        </div>
        <div className="pb-6 text-center text-xs text-neutral-500 dark:text-neutral-500">
          &copy; {new Date().getFullYear()} Kolega Studio. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

function PlanCard({ plan }: { plan: DisplayPlan }) {
  return (
    <div
      className={`relative flex h-full flex-col rounded-xl border p-6 ${
        plan.featured
          ? 'border-brand-primary/50 bg-brand-primary/5'
          : plan.comingSoon
          ? 'border-slate-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 opacity-75'
          : 'border-slate-300 dark:border-neutral-700 bg-white dark:bg-neutral-900'
      }`}
    >
      {plan.featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="rounded-full bg-brand-primary px-3 py-1 text-xs font-medium text-black">Most Popular</span>
        </div>
      )}
      {plan.comingSoon && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="rounded-full bg-neutral-200 dark:bg-neutral-700 px-3 py-1 text-xs font-medium text-neutral-700 dark:text-neutral-300">
            Coming Soon
          </span>
        </div>
      )}

      <h3 className="text-xl font-bold text-neutral-900 dark:text-white">{plan.name}</h3>
      <p className="mt-2 min-h-[3rem] text-sm text-neutral-600 dark:text-neutral-400">{plan.description}</p>

      <div className="mt-4 flex items-baseline gap-1">
        <span className="text-4xl font-extrabold text-neutral-900 dark:text-white">{plan.priceLabel}</span>
        {plan.cadence && <span className="text-sm text-neutral-600 dark:text-neutral-400">{plan.cadence}</span>}
      </div>
      {plan.priceLabel.startsWith('$') && plan.priceLabel !== '$0' && (
        <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-500">
          USD, billed monthly. Taxes calculated at checkout.
        </p>
      )}

      <div className="mt-6 border-t border-neutral-200 dark:border-neutral-800 pt-5" />

      <ul className="mt-4 flex-grow space-y-3">
        {plan.features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm text-neutral-600 dark:text-neutral-400">
            <Check size={16} className="mt-0.5 flex-shrink-0 text-green-500 dark:text-green-400" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      {plan.comingSoon ? (
        <span className="mt-6 block cursor-not-allowed rounded-lg bg-neutral-100 dark:bg-neutral-800 px-4 py-3 text-center text-sm font-semibold text-neutral-500 dark:text-neutral-400">
          Coming Soon
        </span>
      ) : (
        <Link
          to={plan.cta.href}
          className={`mt-6 block rounded-lg px-4 py-3 text-center text-sm font-semibold transition-colors ${
            plan.featured
              ? 'bg-brand-primary text-black hover:bg-brand-primaryDark'
              : 'border border-slate-300 dark:border-neutral-600 text-neutral-900 dark:text-white hover:border-brand-primary hover:text-brand-primaryDark dark:hover:text-brand-primary'
          }`}
        >
          {plan.cta.label}
        </Link>
      )}
    </div>
  );
}

export default function PricingPage() {
  const { tiers, loading, error } = usePublicPricingTiers();

  // Only tiers that are open for signup are shown, except sales-led tiers
  // (requires_sales_contact), which render with a Contact Sales CTA instead.
  const plans: DisplayPlan[] = (tiers || [])
    .filter((t) => t.metadata?.signup_enabled !== false || t.metadata?.requires_sales_contact)
    .map(tierToPlan);

  return (
    <PublicPageShell>
      <main className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mb-10 text-center">
          <h1 className="text-3xl font-bold sm:text-4xl">Pricing</h1>
          <p className="mx-auto mt-3 max-w-2xl text-neutral-600 dark:text-neutral-400">
            Kolega Studio — build and ship full-stack web apps with AI agents. Subscriptions include
            monthly usage credits that power your AI builds.
          </p>
          <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-500">{TAX_NOTE}</p>
          <p className="mx-auto mt-2 max-w-2xl text-sm text-neutral-500 dark:text-neutral-500">{TRIAL_NOTE}</p>
        </div>

        {loading && (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
          </div>
        )}

        {error && !loading && (
          <p className="py-16 text-center text-neutral-600 dark:text-neutral-400">
            Unable to load pricing right now. Please try again later.
          </p>
        )}

        {!loading && !error && (
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2 lg:grid-cols-3">
            {plans.map((plan) => (
              <PlanCard key={plan.name} plan={plan} />
            ))}
          </div>
        )}
      </main>
    </PublicPageShell>
  );
}
